import { SelectionKey } from '../components/data-table/data-source/data-source.types';
import { SelectionState } from '../services/table-selection.service';

export interface ResolveSelectedRowsOptions<TRow> {
  state: SelectionState;
  currentPageRows: TRow[];
  cachedRows: TRow[];
  getRowId: (row: TRow) => SelectionKey;
  normalizeRowId?: (id: SelectionKey) => SelectionKey;
}

export function resolveSelectedRowsFromTableState<TRow>(
  options: ResolveSelectedRowsOptions<TRow>
): TRow[] {
  const {
    state,
    currentPageRows,
    cachedRows,
    getRowId,
    normalizeRowId = id => id,
  } = options;

  if (state.mode === 'all') {
    const excluded = new Set(
      [...state.excludedIds].map(id => normalizeRowId(id))
    );
    return currentPageRows.filter(row => {
      if (row == null) {
        return false;
      }
      return !excluded.has(normalizeRowId(getRowId(row)));
    });
  }

  if (state.mode === 'none' || !currentPageRows.length) {
    return [];
  }

  const selected = new Set(
    [...state.selectedIds].map(id => normalizeRowId(id))
  );
  const fromCurrentPage = currentPageRows.filter(
    row => row != null && selected.has(normalizeRowId(getRowId(row)))
  );

  if (state.mode === 'partial') {
    const fromCache = cachedRows.filter(
      row =>
        row != null &&
        getRowId(row) != null &&
        selected.has(normalizeRowId(getRowId(row)))
    );
    if (fromCache.length > 0) {
      const merged = new Map<SelectionKey, TRow>();
      for (const row of fromCache) {
        merged.set(normalizeRowId(getRowId(row)), row);
      }
      for (const row of fromCurrentPage) {
        merged.set(normalizeRowId(getRowId(row)), row);
      }
      return [...merged.values()];
    }
  }

  return fromCurrentPage;
}

export function hasCrossPageTableSelection(
  state: SelectionState,
  totalCount: number
): boolean {
  if (state.mode === 'none') {
    return false;
  }
  if (state.mode === 'all') {
    return totalCount > state.excludedIds.size;
  }
  return state.selectedIds.size > 0;
}

/** PAC-24418: cross-page select-all → SPW warning + current-page export only (not full result). */
export function hasSummaryPageExportRowSelection(
  hasRowSelection: boolean,
  selectionState?: SelectionState
): boolean {
  if (selectionState?.mode === 'all') {
    return false;
  }
  return hasRowSelection;
}

export function isSelectAllAcrossPagesMode(
  selectionState?: SelectionState
): boolean {
  return selectionState?.mode === 'all';
}
